import { useCallback, useEffect, useMemo, useState } from 'react';

import type { CourseListItem } from './useCourses';

export interface CourseListPreferences {
  showArchived: boolean;
  showComplete: boolean;
  showLive: boolean;
  sortBy: 'created' | 'lastUpdated';
  sortDirection: 'asc' | 'desc';
}

type CourseFilterKey = 'showArchived' | 'showComplete' | 'showLive';

const DEFAULT_COURSE_LIST_PREFERENCES: CourseListPreferences = {
  showArchived: false,
  showComplete: true,
  showLive: true,
  sortBy: 'created',
  sortDirection: 'desc',
};

function getStorageKey(userId: string | null | undefined) {
  if (!userId) {
    return null;
  }

  return `course-list-preferences:${userId}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeBoolean(value: unknown, fallback: boolean) {
  return typeof value === 'boolean' ? value : fallback;
}

function normalizeSortBy(value: unknown): CourseListPreferences['sortBy'] {
  return value === 'lastUpdated' ? 'lastUpdated' : 'created';
}

function normalizeSortDirection(
  value: unknown,
): CourseListPreferences['sortDirection'] {
  return value === 'asc' ? 'asc' : 'desc';
}

export function getDefaultCourseListPreferences(): CourseListPreferences {
  return { ...DEFAULT_COURSE_LIST_PREFERENCES };
}

export function readCourseListPreferences(
  userId: string | null | undefined,
): CourseListPreferences {
  const storageKey = getStorageKey(userId);

  if (!storageKey || typeof window === 'undefined') {
    return getDefaultCourseListPreferences();
  }

  const rawValue = window.localStorage.getItem(storageKey);

  if (!rawValue) {
    return getDefaultCourseListPreferences();
  }

  try {
    const parsed = JSON.parse(rawValue);

    if (!isRecord(parsed)) {
      return getDefaultCourseListPreferences();
    }

    return {
      showArchived: normalizeBoolean(
        parsed.showArchived,
        DEFAULT_COURSE_LIST_PREFERENCES.showArchived,
      ),
      showComplete: normalizeBoolean(
        parsed.showComplete,
        DEFAULT_COURSE_LIST_PREFERENCES.showComplete,
      ),
      showLive: normalizeBoolean(
        parsed.showLive,
        DEFAULT_COURSE_LIST_PREFERENCES.showLive,
      ),
      sortBy: normalizeSortBy(parsed.sortBy),
      sortDirection: normalizeSortDirection(parsed.sortDirection),
    };
  } catch {
    return getDefaultCourseListPreferences();
  }
}

function getCreatedSortValue(course: CourseListItem) {
  return course.creationIndex;
}

function getLastUpdatedSortValue(course: CourseListItem) {
  return course.lastUpdatedTimeSeconds;
}

function compareNullableNumbers(
  left: number | null,
  right: number | null,
  direction: CourseListPreferences['sortDirection'],
) {
  if (left === null && right === null) {
    return 0;
  }

  if (left === null) {
    return 1;
  }

  if (right === null) {
    return -1;
  }

  if (left === right) {
    return 0;
  }

  return direction === 'asc' ? left - right : right - left;
}

export function matchesCourseFilters(
  course: CourseListItem,
  preferences: CourseListPreferences,
) {
  const matchesArchived = preferences.showArchived && course.isArchived;
  const matchesComplete =
    preferences.showComplete && !course.isArchived && course.isComplete === true;
  const matchesLive =
    preferences.showLive && !course.isArchived && course.isComplete !== true;

  return matchesArchived || matchesComplete || matchesLive;
}

export function sortCourses(
  courses: CourseListItem[],
  preferences: CourseListPreferences,
) {
  const sortedCourses = [...courses];

  sortedCourses.sort((left, right) => {
    if (preferences.sortBy === 'lastUpdated') {
      const updatedComparison = compareNullableNumbers(
        getLastUpdatedSortValue(left),
        getLastUpdatedSortValue(right),
        preferences.sortDirection,
      );

      if (updatedComparison !== 0) {
        return updatedComparison;
      }
    } else {
      const createdComparison = compareNullableNumbers(
        getCreatedSortValue(left),
        getCreatedSortValue(right),
        preferences.sortDirection,
      );

      if (createdComparison !== 0) {
        return createdComparison;
      }
    }

    return right.creationIndex - left.creationIndex;
  });

  return sortedCourses;
}

export function applyCourseListPreferences(
  courses: CourseListItem[],
  preferences: CourseListPreferences,
) {
  return sortCourses(
    courses.filter((course) => matchesCourseFilters(course, preferences)),
    preferences,
  );
}

export function useCourseListPreferences(userId: string | null | undefined) {
  const [preferences, setPreferences] = useState<CourseListPreferences>(() =>
    readCourseListPreferences(userId),
  );

  useEffect(() => {
    setPreferences(readCourseListPreferences(userId));
  }, [userId]);

  useEffect(() => {
    const storageKey = getStorageKey(userId);

    if (!storageKey || typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(storageKey, JSON.stringify(preferences));
  }, [preferences, userId]);

  const setSortBy = useCallback((sortBy: CourseListPreferences['sortBy']) => {
    setPreferences((current) => {
      if (current.sortBy === sortBy) {
        return current;
      }

      return {
        ...current,
        sortBy,
      };
    });
  }, []);

  const toggleSortDirection = useCallback(() => {
    setPreferences((current) => ({
      ...current,
      sortDirection: current.sortDirection === 'desc' ? 'asc' : 'desc',
    }));
  }, []);

  const toggleFilter = useCallback((filterKey: CourseFilterKey) => {
    setPreferences((current) => ({
      ...current,
      [filterKey]: !current[filterKey],
    }));
  }, []);

  return useMemo(
    () => ({
      preferences,
      setSortBy,
      toggleSortDirection,
      toggleFilter,
    }),
    [preferences, setSortBy, toggleFilter, toggleSortDirection],
  );
}
